/**
 * Residue grouping — a TSX port of the engine CLI's `_grouped`
 * (ncoder-playground `app/migration_engine/runbook.py`).
 *
 * The first real-org Profile produced 38 `UNKNOWN_TAB` residue entries — standard tabs
 * for features the org has not enabled — each repeating the same manual step. Printing
 * that sentence 38 times does not make the report more honest, it makes the other three
 * entries unfindable, and a report nobody finishes reading is a report nobody acts on.
 *
 * So the manual step is printed ONCE per `(code, manual_step)` group with a count, and
 * every target is still listed individually underneath it. Nothing is summarised away:
 * grouping changes no data, only the rendering. Group order is first appearance, and
 * entries arrive sorted by the contract, so two runs of the same plan render identically.
 */

/** The minimum an item needs to be groupable. Residue and dropped effects both qualify. */
export interface GroupableByCode {
  code?: string;
  manual_step?: string;
}

export interface CodeGroup<T> {
  code: string;
  manual_step: string;
  count: number;
  items: T[];
}

export function groupByCodeAndStep<T extends GroupableByCode>(items: T[]): CodeGroup<T>[] {
  const groups = new Map<string, CodeGroup<T>>();
  for (const item of items) {
    const code = item.code ?? "";
    const manual_step = item.manual_step ?? "";
    // Tab-joined: neither field can contain a tab, so no two distinct pairs collide.
    const id = `${code}\t${manual_step}`;
    const existing = groups.get(id);
    if (existing) {
      existing.items.push(item);
      existing.count += 1;
    } else {
      groups.set(id, { code, manual_step, count: 1, items: [item] });
    }
  }
  return [...groups.values()];
}

/** `UNKNOWN_TAB × 38`, or just `UNKNOWN_TAB` when the group holds one item. */
export function groupHeading(group: CodeGroup<unknown>): string {
  const code = group.code || "(no code)";
  return group.count > 1 ? `${code} × ${group.count}` : code;
}
